// Marketing content and copy data for ForgeNetwork.pro
// Based on elite direct-response marketing principles

export const siteConfig = {
  name: 'ForgeNetwork',
  tagline: 'Ship Faster. Break Nothing. Scale Everything.',
  description:
    'The unified AI-powered developer productivity ecosystem that transforms code into clarity.',
  url: 'https://forgenetwork.pro',
  ogImage: 'https://forgenetwork.pro/og.png',
};

export const navigation = {
  main: [
    { name: 'Platform', href: '/platform' },
    { name: 'Products', href: '/products' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Enterprise', href: '/enterprise' },
    { name: 'About', href: '/about' },
  ],
  products: [
    {
      name: 'CodeSpringForge',
      href: '/products/codespringforge',
      description: 'AI-powered code analysis and PRD generation',
    },
    {
      name: 'AICodeForge',
      href: '/products/aicodeforge',
      description: 'Intelligent code generation and refactoring',
      comingSoon: true,
    },
    {
      name: 'SDD-Forge',
      href: '/products/sdd-forge',
      description: 'Software design document automation',
      comingSoon: true,
    },
  ],
  footer: {
    product: [
      { name: 'Platform', href: '/platform' },
      { name: 'Products', href: '/products' },
      { name: 'Pricing', href: '/pricing' },
      { name: 'Enterprise', href: '/enterprise' },
      { name: 'Changelog', href: '/changelog' },
    ],
    company: [
      { name: 'About', href: '/about' },
      { name: 'Blog', href: '/blog' },
      { name: 'Careers', href: '/careers' },
      { name: 'Contact', href: '/contact' },
    ],
    resources: [
      { name: 'Documentation', href: '/docs' },
      { name: 'API Reference', href: '/docs/api' },
      { name: 'Status', href: '/status' },
      { name: 'Community', href: '/community' },
    ],
    legal: [
      { name: 'Privacy', href: '/privacy' },
      { name: 'Terms', href: '/terms' },
      { name: 'Security', href: '/security' },
    ],
  },
};

export const hero = {
  badge: 'Now in Public Beta',
  headline: 'Ship Faster.\nBreak Nothing.\nScale Everything.',
  subheadline:
    'ForgeNetwork transforms your codebase into actionable intelligence. Analyze repositories, extract features, generate PRDs, and accelerate development with AI-powered precision.',
  primaryCTA: { text: 'Start Free', href: '/register' },
  secondaryCTA: { text: 'See Demo', href: '/demo' },
  stats: [
    { value: '10x', label: 'Faster PRD Generation' },
    { value: '47%', label: 'Reduction in Tech Debt' },
    { value: '99.9%', label: 'Uptime SLA' },
  ],
};

export const problems = [
  {
    title: 'Documentation Decay',
    description:
      'Your docs are always out of sync. PRDs are stale before the sprint ends.',
    icon: 'FileWarning',
  },
  {
    title: 'Knowledge Silos',
    description:
      'Critical context lives in one engineer\'s head. Onboarding takes months.',
    icon: 'Brain',
  },
  {
    title: 'Analysis Paralysis',
    description:
      'Weeks spent understanding legacy code before making a single change.',
    icon: 'Clock',
  },
  {
    title: 'Scaling Bottleneck',
    description:
      'Growth stalls because your architecture can\'t keep pace with your ambition.',
    icon: 'TrendingDown',
  },
];

export const solution = {
  title: 'The Forge Pipeline',
  subtitle: 'From Code to Clarity in Minutes',
  description:
    'Our proprietary AI pipeline analyzes your entire codebase, extracts actionable features, and generates production-ready documentation that actually stays current.',
  steps: [
    {
      number: '01',
      title: 'Connect',
      description:
        'Link your repositories from GitHub, GitLab, or Bitbucket. We support any tech stack.',
    },
    {
      number: '02',
      title: 'Analyze',
      description:
        'Our AI scans your codebase, understanding architecture, patterns, and dependencies.',
    },
    {
      number: '03',
      title: 'Extract',
      description:
        'Features, capabilities, and technical debt are automatically identified and cataloged.',
    },
    {
      number: '04',
      title: 'Generate',
      description:
        'Get instant PRDs, architecture diagrams, and actionable recommendations.',
    },
  ],
};

export const features = [
  {
    title: 'Repository Analysis',
    description:
      'Deep-dive into any codebase. Understand architecture, identify patterns, and surface hidden complexity.',
    icon: 'GitBranch',
    tier: 'free',
  },
  {
    title: 'Feature Extraction',
    description:
      'Automatically catalog every capability in your codebase with AI-powered precision.',
    icon: 'Layers',
    tier: 'free',
  },
  {
    title: 'PRD Generation',
    description:
      'Transform code into comprehensive product requirement documents in seconds.',
    icon: 'FileText',
    tier: 'free',
  },
  {
    title: 'Security Scanning',
    description:
      'Identify vulnerabilities, OWASP risks, and compliance gaps before they become incidents.',
    icon: 'Shield',
    tier: 'pro',
  },
  {
    title: 'Architecture Visualization',
    description:
      'Generate interactive diagrams that reveal how your systems actually work together.',
    icon: 'Network',
    tier: 'pro',
  },
  {
    title: 'LLM-Powered Assistance',
    description:
      'Ask questions about your codebase and get instant, context-aware answers.',
    icon: 'MessageSquare',
    tier: 'pro',
  },
  {
    title: 'Multi-Repo Analysis',
    description:
      'Analyze entire ecosystems. Understand dependencies across your organization.',
    icon: 'FolderGit2',
    tier: 'enterprise',
  },
  {
    title: 'Custom Integrations',
    description:
      'Connect to Jira, Linear, Notion, and your existing development workflow.',
    icon: 'Puzzle',
    tier: 'enterprise',
  },
];

export const pricing = {
  tiers: [
    {
      name: 'Starter',
      price: 'Free',
      period: '',
      description: 'Perfect for exploring what ForgeNetwork can do',
      features: [
        '3 repository analyses per month',
        'Basic feature extraction',
        'PRD generation (Markdown)',
        'Community support',
      ],
      cta: 'Start Free',
      href: '/register',
      highlighted: false,
    },
    {
      name: 'Pro',
      price: '$29',
      period: '/month',
      description: 'For teams shipping production software',
      features: [
        'Unlimited repository analyses',
        'Advanced feature extraction',
        'PRD export (Markdown, JSON, PDF)',
        'Security scanning',
        'Architecture visualization',
        'LLM-powered Q&A',
        'Priority support',
        'API access',
      ],
      cta: 'Start 14-Day Trial',
      href: '/register?plan=pro',
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: '$99',
      period: '/seat/month',
      description: 'For organizations with complex requirements',
      features: [
        'Everything in Pro',
        'Multi-repo analysis',
        'Custom integrations',
        'SSO/SAML authentication',
        'Audit logs & compliance',
        'Dedicated support',
        'SLA guarantee',
        'Custom deployment options',
      ],
      cta: 'Contact Sales',
      href: '/contact?type=enterprise',
      highlighted: false,
    },
  ],
  faq: [
    {
      question: 'Can I try ForgeNetwork before committing?',
      answer:
        'Absolutely. Start with our free tier to explore the platform. Pro plans include a 14-day trial with full feature access.',
    },
    {
      question: 'What happens to my code?',
      answer:
        'Your code never leaves your control. We analyze code in memory and store only metadata. All data is encrypted at rest and in transit.',
    },
    {
      question: 'Can I cancel anytime?',
      answer:
        'Yes, you can cancel your subscription at any time. You\'ll retain access until the end of your billing period.',
    },
    {
      question: 'Do you offer discounts for startups?',
      answer:
        'Yes! Qualified startups get 50% off Pro for the first year. Contact us to learn more.',
    },
  ],
};

export const testimonials = [
  {
    quote:
      'The AI-powered analysis helped us identify architectural patterns we didn\'t even know existed in our codebase.',
    author: 'Beta User',
    role: 'Engineering Lead',
    company: 'Series B Startup',
    avatar: '/testimonials/user1.jpg',
  },
  {
    quote:
      'PRD generation from actual code is exactly what we needed. No more outdated documentation.',
    author: 'Early Adopter',
    role: 'Technical Architect',
    company: 'SaaS Company',
    avatar: '/testimonials/user2.jpg',
  },
  {
    quote:
      'The security scanning caught vulnerabilities our manual reviews had missed for months.',
    author: 'Beta Tester',
    role: 'Senior Developer',
    company: 'FinTech Startup',
    avatar: '/testimonials/user3.jpg',
  },
];

export const socialProof = {
  trustedBy: 'Used by teams building the future',
  logos: [] as { name: string; logo?: string }[], // Will add real customer logos as we grow
  stats: {
    repositories: '500+',
    features: '25,000+',
    prds: '1,200+',
  },
};

export const cta = {
  primary: {
    headline: 'Ready to Transform Your Development Workflow?',
    subheadline:
      'Join thousands of engineering teams shipping better software, faster.',
    buttonText: 'Start Free Today',
    buttonHref: '/register',
  },
  urgency: {
    headline: 'Limited Beta Pricing',
    subheadline:
      'Lock in founding member rates before we exit beta. Early adopters keep these prices forever.',
    buttonText: 'Claim Your Spot',
    buttonHref: '/register?ref=beta',
  },
};

export const products = {
  codespringforge: {
    name: 'CodeSpringForge',
    tagline: 'From Code to Clarity',
    description:
      'AI-powered code analysis and PRD generation that transforms how your team understands and documents software.',
    features: [
      {
        title: 'Intelligent Code Analysis',
        description:
          'Our AI understands your codebase at the architectural level, identifying patterns, dependencies, and potential issues.',
        icon: 'Search',
      },
      {
        title: 'Automated Feature Extraction',
        description:
          'Every capability in your codebase is automatically cataloged, tagged, and made searchable.',
        icon: 'Layers',
      },
      {
        title: 'PRD Generation',
        description:
          'Generate comprehensive product requirement documents that reflect the actual state of your software.',
        icon: 'FileText',
      },
      {
        title: 'Export Flexibility',
        description:
          'Export to Markdown, JSON, or PDF. Integrate with your existing documentation workflow.',
        icon: 'Download',
      },
    ],
    capabilities: [
      { name: 'Repository Management', tier: 'basic' },
      { name: 'Code Analysis', tier: 'basic' },
      { name: 'Feature Extraction', tier: 'basic' },
      { name: 'PRD Generation', tier: 'basic' },
      { name: 'Security Scanning', tier: 'security' },
      { name: 'Dependency Scanning', tier: 'security' },
      { name: 'SBOM Generation', tier: 'security' },
      { name: 'Threat Modeling', tier: 'security' },
      { name: 'Architecture Visualization', tier: 'pro' },
      { name: 'Multi-Repo Analysis', tier: 'pro' },
      { name: 'LLM Assistance', tier: 'pro' },
      { name: 'Integrations', tier: 'pro' },
      { name: 'Custom Security Rules', tier: 'enterprise' },
      { name: 'Compliance Reporting', tier: 'enterprise' },
      { name: 'Advanced Search', tier: 'enterprise' },
    ],
  },
};

export const affiliates = {
  headline: 'Earn 30% Recurring Commission',
  subheadline:
    'Join our affiliate program and earn every time someone you refer becomes a customer.',
  benefits: [
    {
      title: '30% Recurring',
      description: 'Earn 30% of every payment, for the lifetime of the customer.',
    },
    {
      title: '90-Day Cookie',
      description: 'Long attribution window means you get credit for your referrals.',
    },
    {
      title: 'Monthly Payouts',
      description: 'Get paid every month via PayPal or bank transfer.',
    },
    {
      title: 'Real-Time Dashboard',
      description: 'Track clicks, conversions, and earnings in real-time.',
    },
  ],
  cta: {
    text: 'Join Affiliate Program',
    href: '/register?type=affiliate',
  },
};

export const enterprise = {
  headline: 'ForgeNetwork for Enterprise',
  subheadline:
    'Built for organizations with complex requirements and high security standards.',
  features: [
    {
      title: 'Self-Hosted Deployment',
      description:
        'Run ForgeNetwork in your own cloud or on-premises infrastructure.',
      icon: 'Server',
    },
    {
      title: 'SSO & SAML',
      description:
        'Integrate with your existing identity provider. Okta, Azure AD, and more.',
      icon: 'Key',
    },
    {
      title: 'Audit Logs',
      description:
        'Complete visibility into every action taken in your organization.',
      icon: 'FileSearch',
    },
    {
      title: 'Compliance Ready',
      description:
        'SOC 2, HIPAA, and GDPR compliant. Enterprise-grade security certifications.',
      icon: 'BadgeCheck',
    },
    {
      title: 'Dedicated Support',
      description:
        'Priority support with dedicated account manager and Slack channel.',
      icon: 'Headphones',
    },
    {
      title: 'SLA Guarantee',
      description:
        '99.9% uptime SLA with financial backing. We stake our reputation on reliability.',
      icon: 'Clock',
    },
  ],
  cta: {
    text: 'Contact Sales',
    href: '/contact?type=enterprise',
  },
};
